(() => {
  'use strict';

  const SERVICE_LABELS = {
    'Ecommerce Online Store': 'Online Store',
    'Full-Service Repair Diagnostics Shop': 'Full-Service Repair',
    'Performance Shop': 'Performance Shop',
    'Mobile Service Repair': 'Mobile Repair',
    'Tow Service': 'Towing',
    'Dyno Services': 'Dyno Services',
    'Powersports Performance & Repair': 'Powersports'
  };

  const milesBetween = (a, b) => {
    const toRadians = value => value * Math.PI / 180;
    const earthRadiusMiles = 3958.7613;
    const latDelta = toRadians(b.latitude - a.latitude);
    const lonDelta = toRadians(b.longitude - a.longitude);
    const lat1 = toRadians(a.latitude);
    const lat2 = toRadians(b.latitude);
    const haversine = Math.sin(latDelta / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(lonDelta / 2) ** 2;
    return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const formatAddress = dealer => [
    dealer.address1,
    dealer.address2,
    [dealer.city, dealer.region].filter(Boolean).join(', '),
    dealer.postalCode,
    dealer.country !== 'United States' ? dealer.country : ''
  ].filter(Boolean).join(' · ');

  const directionsUrl = dealer => {
    const destination = [dealer.address1, dealer.address2, dealer.city, dealer.region, dealer.postalCode, dealer.country].filter(Boolean).join(', ');
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  };

  class PPEIDealerLocator {
    constructor(root) {
      this.root = root;
      this.dealers = Array.isArray(window.PPEI_DEALERS) ? window.PPEI_DEALERS.filter(item => Number.isFinite(item.latitude) && Number.isFinite(item.longitude)) : [];
      this.defaultRadius = Number(root.dataset.defaultRadius || 50);
      this.radius = this.defaultRadius;
      this.country = 'all';
      this.activeServices = new Set();
      this.origin = null;
      this.originMarker = null;
      this.map = null;
      this.popup = null;
      this.filtered = [];
      this.visibleCards = 24;
      this.selectedId = null;
      this.cacheElements();
      this.bindEvents();
      this.buildServiceFilters();
      this.render();
      this.waitForMapLibre();
    }

    cacheElements() {
      this.searchForm = this.root.querySelector('[data-locator-search]');
      this.postalInput = this.root.querySelector('[data-locator-postal]');
      this.countrySelect = this.root.querySelector('[data-locator-country]');
      this.searchButton = this.root.querySelector('[data-locator-search-button]');
      this.locationButton = this.root.querySelector('[data-locator-location]');
      this.clearButton = this.root.querySelector('[data-locator-clear]');
      this.radiusContainer = this.root.querySelector('[data-locator-radii]');
      this.servicesContainer = this.root.querySelector('[data-locator-services]');
      this.status = this.root.querySelector('[data-locator-status]');
      this.error = this.root.querySelector('[data-locator-error]');
      this.list = this.root.querySelector('[data-locator-list]');
      this.loadMoreButton = this.root.querySelector('[data-locator-load-more]');
      this.mapElement = this.root.querySelector('[data-locator-map]');
      this.mapMessage = this.root.querySelector('[data-locator-map-message]');
    }

    bindEvents() {
      this.searchForm.addEventListener('submit', event => {
        event.preventDefault();
        this.searchPostalCode();
      });
      this.locationButton.addEventListener('click', () => this.useMyLocation());
      this.clearButton.addEventListener('click', () => this.reset());
      this.countrySelect.addEventListener('change', () => {
        this.country = this.countrySelect.value;
        this.visibleCards = 24;
        this.render();
      });
      this.radiusContainer.addEventListener('click', event => {
        const button = event.target.closest('[data-radius]');
        if (!button) return;
        this.radius = button.dataset.radius === 'all' ? Infinity : Number(button.dataset.radius);
        this.radiusContainer.querySelectorAll('[data-radius]').forEach(item => item.classList.toggle('is-active', item === button));
        this.visibleCards = 24;
        this.render();
      });
      this.servicesContainer.addEventListener('click', event => {
        const button = event.target.closest('[data-service]');
        if (!button) return;
        const service = button.dataset.service;
        if (this.activeServices.has(service)) this.activeServices.delete(service);
        else this.activeServices.add(service);
        button.classList.toggle('is-active', this.activeServices.has(service));
        button.setAttribute('aria-pressed', String(this.activeServices.has(service)));
        this.visibleCards = 24;
        this.render();
      });
      this.loadMoreButton.addEventListener('click', () => {
        this.visibleCards += 24;
        this.renderCards();
      });
    }

    buildServiceFilters() {
      const services = [...new Set(this.dealers.flatMap(dealer => dealer.services))]
        .sort((a, b) => (SERVICE_LABELS[a] || a).localeCompare(SERVICE_LABELS[b] || b));
      services.forEach(service => {
        const button = element('button', 'ppei-locator__chip', SERVICE_LABELS[service] || service);
        button.type = 'button';
        button.dataset.service = service;
        button.setAttribute('aria-pressed', 'false');
        this.servicesContainer.appendChild(button);
      });
    }

    waitForMapLibre(attempt = 0) {
      if (window.maplibregl) {
        this.initializeMap();
        return;
      }
      if (attempt > 100) {
        this.mapMessage.textContent = 'The map could not load. Dealer results are still available in the list.';
        return;
      }
      window.setTimeout(() => this.waitForMapLibre(attempt + 1), 50);
    }

    initializeMap() {
      if (this.map || !this.mapElement) return;
      this.map = new window.maplibregl.Map({
        container: this.mapElement,
        style: 'https://tiles.openfreemap.org/styles/liberty',
        center: [-98.5, 39.5],
        zoom: 3,
        minZoom: 1.5,
        maxZoom: 18,
        cooperativeGestures: true,
        attributionControl: true
      });
      this.map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      this.map.on('load', () => {
        this.mapMessage.hidden = true;
        this.map.addSource('ppei-dealers', {
          type: 'geojson', data: this.toGeoJSON(this.filtered), cluster: true, clusterMaxZoom: 13, clusterRadius: 45
        });
        this.map.addLayer({
          id: 'ppei-clusters', type: 'circle', source: 'ppei-dealers', filter: ['has', 'point_count'],
          paint: {
            'circle-color': ['step', ['get', 'point_count'], '#dca51d', 25, '#efbd3e', 100, '#f5d26f'],
            'circle-radius': ['step', ['get', 'point_count'], 18, 25, 24, 100, 31],
            'circle-stroke-width': 2, 'circle-stroke-color': '#0a0a0a'
          }
        });
        this.map.addLayer({
          id: 'ppei-cluster-count', type: 'symbol', source: 'ppei-dealers', filter: ['has', 'point_count'],
          layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 12 },
          paint: { 'text-color': '#0a0a0a' }
        });
        this.map.addLayer({
          id: 'ppei-points', type: 'circle', source: 'ppei-dealers', filter: ['!', ['has', 'point_count']],
          paint: { 'circle-color': '#0a0a0a', 'circle-radius': 7, 'circle-stroke-width': 3, 'circle-stroke-color': '#dca51d' }
        });
        this.map.on('click', 'ppei-clusters', event => {
          const feature = this.map.queryRenderedFeatures(event.point, { layers: ['ppei-clusters'] })[0];
          if (!feature) return;
          this.map.getSource('ppei-dealers').getClusterExpansionZoom(feature.properties.cluster_id).then(zoom => {
            this.map.easeTo({ center: feature.geometry.coordinates, zoom });
          });
        });
        this.map.on('click', 'ppei-points', event => {
          const feature = event.features && event.features[0];
          if (!feature) return;
          this.openDealer(String(feature.properties.id));
        });
        ['ppei-clusters', 'ppei-points'].forEach(layer => {
          this.map.on('mouseenter', layer, () => { this.map.getCanvas().style.cursor = 'pointer'; });
          this.map.on('mouseleave', layer, () => { this.map.getCanvas().style.cursor = ''; });
        });
        this.updateMapData(true);
      });
    }

    toGeoJSON(dealers) {
      return {
        type: 'FeatureCollection',
        features: dealers.map(dealer => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [dealer.longitude, dealer.latitude] },
          properties: { id: dealer.id, name: dealer.name }
        }))
      };
    }

    getFilteredDealers() {
      return this.dealers
        .map(dealer => ({ ...dealer, distance: this.origin ? milesBetween(this.origin, dealer) : null }))
        .filter(dealer => {
          if (this.country === 'us' && dealer.country !== 'United States') return false;
          if (this.country === 'ca' && dealer.country !== 'Canada') return false;
          if (this.country === 'intl' && ['United States', 'Canada'].includes(dealer.country)) return false;
          if (this.activeServices.size && !dealer.services.some(service => this.activeServices.has(service))) return false;
          if (this.origin && Number.isFinite(this.radius) && dealer.distance > this.radius) return false;
          return true;
        })
        .sort((a, b) => this.origin ? a.distance - b.distance : a.name.localeCompare(b.name));
    }

    render() {
      this.filtered = this.getFilteredDealers();
      this.status.innerHTML = '';
      const strong = element('strong', '', `${this.filtered.length} dealer${this.filtered.length === 1 ? '' : 's'}`);
      this.status.append(strong, document.createTextNode(this.origin ? ` found near ${this.origin.label}` : ' available'));
      this.clearButton.hidden = !this.origin && this.country === 'all' && !this.activeServices.size && this.radius === this.defaultRadius;
      this.renderCards();
      this.updateMapData(false);
    }

    renderCards() {
      this.list.innerHTML = '';
      const dealers = this.filtered.slice(0, this.visibleCards);
      if (!dealers.length) {
        this.list.appendChild(element('p', 'ppei-locator__empty', 'No dealers match these filters. Try a larger radius or clear a filter.'));
      }
      dealers.forEach(dealer => this.list.appendChild(this.createCard(dealer)));
      this.loadMoreButton.hidden = this.visibleCards >= this.filtered.length;
      if (!this.loadMoreButton.hidden) this.loadMoreButton.textContent = `Show more (${this.filtered.length - this.visibleCards} remaining)`;
    }

    createCard(dealer) {
      const card = element('article', 'ppei-locator__card');
      card.dataset.dealerId = dealer.id;
      if (dealer.id === this.selectedId) card.classList.add('is-selected');
      const top = element('div', 'ppei-locator__card-top');
      const title = element('button', 'ppei-locator__card-title', dealer.name);
      title.type = 'button';
      title.addEventListener('click', () => this.openDealer(dealer.id));
      top.appendChild(title);
      if (dealer.distance !== null) top.appendChild(element('span', 'ppei-locator__distance', `${dealer.distance.toFixed(dealer.distance < 10 ? 1 : 0)} mi`));
      card.append(top, element('p', 'ppei-locator__address', formatAddress(dealer)));

      const tags = element('div', 'ppei-locator__tags');
      dealer.services.slice(0, 3).forEach(service => tags.appendChild(element('span', 'ppei-locator__tag', SERVICE_LABELS[service] || service)));
      if (dealer.services.length > 3) tags.appendChild(element('span', 'ppei-locator__tag', `+${dealer.services.length - 3} more`));
      card.appendChild(tags);

      const actions = element('div', 'ppei-locator__actions');
      actions.appendChild(this.createLink('Directions', directionsUrl(dealer), true));
      if (dealer.phone) actions.appendChild(this.createLink('Call', `tel:${dealer.phone.replace(/[^+\d]/g, '')}`, false));
      if (dealer.website) actions.appendChild(this.createLink('Website', dealer.website, true));
      card.appendChild(actions);
      return card;
    }

    createLink(label, href, newTab) {
      const link = element('a', 'ppei-locator__link', label);
      link.href = href;
      if (newTab) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      return link;
    }

    openDealer(id) {
      const dealer = this.dealers.find(item => String(item.id) === String(id));
      if (!dealer || !this.map) return;
      this.selectedId = dealer.id;
      this.root.querySelectorAll('[data-dealer-id]').forEach(card => card.classList.toggle('is-selected', card.dataset.dealerId === String(id)));
      this.map.flyTo({ center: [dealer.longitude, dealer.latitude], zoom: Math.max(this.map.getZoom(), 11), essential: true });
      const popupBody = element('div', 'ppei-locator__popup');
      popupBody.append(element('h3', 'ppei-locator__popup-title', dealer.name), element('p', 'ppei-locator__popup-address', formatAddress(dealer)));
      popupBody.appendChild(this.createLink('Get directions', directionsUrl(dealer), true));
      if (this.popup) this.popup.remove();
      this.popup = new window.maplibregl.Popup({ offset: 12, closeButton: true }).setLngLat([dealer.longitude, dealer.latitude]).setDOMContent(popupBody).addTo(this.map);
    }

    updateMapData(initial) {
      if (!this.map || !this.map.getSource('ppei-dealers')) return;
      this.map.getSource('ppei-dealers').setData(this.toGeoJSON(this.filtered));
      if (this.origin) {
        if (this.originMarker) this.originMarker.remove();
        const markerNode = element('div', 'ppei-locator__origin-marker');
        markerNode.style.cssText = 'width:20px;height:20px;border:4px solid #fff;background:#1683ff;border-radius:50%;box-shadow:0 0 0 3px rgba(22,131,255,.35)';
        this.originMarker = new window.maplibregl.Marker({ element: markerNode }).setLngLat([this.origin.longitude, this.origin.latitude]).addTo(this.map);
        this.fitCurrentResults();
      } else if (initial) {
        this.map.fitBounds([[-168, 18], [-50, 73]], { padding: 28, duration: 0 });
      }
    }

    fitCurrentResults() {
      if (!this.map || !this.origin) return;
      const bounds = new window.maplibregl.LngLatBounds([this.origin.longitude, this.origin.latitude], [this.origin.longitude, this.origin.latitude]);
      this.filtered.slice(0, 100).forEach(dealer => bounds.extend([dealer.longitude, dealer.latitude]));
      this.map.fitBounds(bounds, { padding: 64, maxZoom: 10, duration: 700 });
    }

    async searchPostalCode() {
      const raw = this.postalInput.value.trim();
      if (!raw) {
        this.showError('Enter a ZIP or postal code.');
        this.postalInput.focus();
        return;
      }
      this.showError('');
      this.setSearching(true);
      const isCanadian = /[a-z]/i.test(raw);
      const countries = isCanadian ? ['ca', 'us'] : ['us', 'ca'];
      let place = null;
      for (const country of countries) {
        let postal = raw.replace(/\s+/g, '');
        if (country === 'ca') postal = postal.slice(0, 3);
        try {
          const response = await fetch(`https://api.zippopotam.us/${country}/${encodeURIComponent(postal)}`);
          if (!response.ok) continue;
          const data = await response.json();
          if (!data.places || !data.places.length) continue;
          place = {
            latitude: Number(data.places[0].latitude),
            longitude: Number(data.places[0].longitude),
            label: [data.places[0]['place name'], data.places[0]['state abbreviation'], data['post code']].filter(Boolean).join(', ')
          };
          break;
        } catch (error) {
          console.warn('Postal lookup failed', error);
        }
      }
      this.setSearching(false);
      if (!place) {
        this.showError('We could not find that ZIP or postal code. Check it and try again.');
        return;
      }
      this.origin = place;
      this.visibleCards = 24;
      this.render();
    }

    useMyLocation() {
      this.showError('');
      if (!navigator.geolocation) {
        this.showError('Location access is not available in this browser.');
        return;
      }
      this.locationButton.disabled = true;
      this.locationButton.textContent = 'Locating…';
      navigator.geolocation.getCurrentPosition(position => {
        this.origin = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          label: 'your location'
        };
        this.locationButton.disabled = false;
        this.locationButton.textContent = 'Use my location';
        this.visibleCards = 24;
        this.render();
      }, error => {
        this.locationButton.disabled = false;
        this.locationButton.textContent = 'Use my location';
        this.showError(error.code === 1 ? 'Location permission was not granted. Search by ZIP or postal code instead.' : 'We could not determine your location. Search by ZIP or postal code instead.');
      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    }

    setSearching(active) {
      this.searchButton.disabled = active;
      this.searchButton.textContent = active ? 'Searching…' : 'Find dealers';
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    reset() {
      this.origin = null;
      if (this.originMarker) {
        this.originMarker.remove();
        this.originMarker = null;
      }
      this.postalInput.value = '';
      this.country = 'all';
      this.countrySelect.value = 'all';
      this.activeServices.clear();
      this.servicesContainer.querySelectorAll('[data-service]').forEach(button => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      this.radius = this.defaultRadius;
      this.radiusContainer.querySelectorAll('[data-radius]').forEach(button => button.classList.toggle('is-active', Number(button.dataset.radius) === this.defaultRadius));
      this.visibleCards = 24;
      this.showError('');
      this.render();
      if (this.map) this.map.fitBounds([[-168, 18], [-50, 73]], { padding: 28, duration: 500 });
    }
  }

  const initialize = scope => {
    (scope || document).querySelectorAll('[data-ppei-dealer-locator]').forEach(root => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      new PPEIDealerLocator(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document));
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
